import { useEffect, useState } from "react";
import { ShieldCheck, Check, X, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AdminApprovalsPage() {
  const { organizer } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);

  const load = () => {
    api
      .get("/admin/organizers", { params: { status: "pending" } })
      .then((res) => setPending(res.data))
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const act = async (org, action) => {
    setWorking(org.id + action);
    try {
      await api.post(`/admin/organizers/${org.id}/${action}`);
      setPending((prev) => prev.filter((o) => o.id !== org.id));
      toast.success(action === "approve" ? `${org.org_name} er godkjent og kan publisere` : `${org.org_name} er avvist`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking(null);
    }
  };

  if (organizer && !organizer.is_admin) {
    return (
      <DashboardLayout>
        <p className="text-muted-foreground" data-testid="admin-denied">Denne siden krever plattform-administrator.</p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="admin-approvals-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-7 h-7" /> Godkjenninger
          </h1>
          <p className="text-muted-foreground mt-1">
            Nye arrangører som ikke ble automatisk verifisert mot rollene i Brønnøysundregistrene, venter her på manuell godkjenning.
          </p>
        </div>

        {loading ? (
          <Skeleton className="h-24 w-full rounded-xl" />
        ) : pending.length === 0 ? (
          <Card className="border-border">
            <CardContent className="py-10 text-center text-muted-foreground" data-testid="admin-empty-state">
              Ingen ventende godkjenninger – alt er à jour.
            </CardContent>
          </Card>
        ) : (
          pending.map((org) => (
            <Card key={org.id} className="border-border" data-testid={`admin-pending-${org.id}`}>
              <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="font-heading font-semibold flex items-center gap-2">
                    <Building2 className="w-4 h-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">{org.org_name}</span>
                    <Badge variant="secondary">Org.nr {org.org_number}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Oppgitt navn: <span className="font-medium text-foreground">{org.full_name || "–"}</span> · {org.email}
                  </p>
                  <p className="text-xs text-muted-foreground">Registrert {formatDateTime(org.created_at)}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => act(org, "approve")}
                    disabled={!!working}
                    data-testid={`admin-approve-${org.id}`}
                  >
                    {working === org.id + "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Godkjenn</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="text-[hsl(var(--danger))]"
                    onClick={() => act(org, "reject")}
                    disabled={!!working}
                    data-testid={`admin-reject-${org.id}`}
                  >
                    {working === org.id + "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4 mr-1" />Avvis</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
